/**
 * `EncounterHistory` — the per-actor undo/redo authority for the encounter state
 * domain. Unlike roster/game/presence (thin bindings of the generic), this one
 * **extends** `History<S, Cmd, Event>` (`../commandHistory.ts`) because it has
 * *genuine* extra domain behavior on top of the shared machinery: the **retained
 * forward log** and its **rules-free replay**. Those are encounter features, not
 * duplication, so they live here as an override — the base stays domain-agnostic.
 *
 * It binds `applyEncounter` (threading an optional `ctx.compendium` so the shape
 * matches the roster binding and a future reference-dependent encounter rule can
 * read it without reshaping this class), takes
 * `(state, compendium, history, forwardLog)` as named args, and overrides the
 * post-apply hook (`onApplied`) to append a tier-tagged entry per leaf command to
 * the forward log — the one thing the base doesn't (and shouldn't) know about.
 *
 * **`ctx.compendium`.** The encounter *reducer* reads no reference data (the
 * frozen base is snapshotted by the spawn adapter pre-command, so `add_combatant`
 * carries an already-built `Combatant`). The compendium is accepted and threaded
 * anyway for shape-parity with the roster binding; it is optional precisely
 * because no current rule needs it.
 *
 * **Shared encounter undo.** `scopeOf` is overridden (mirroring `../history.ts`'s roster
 * `scopeOfRosterCommand`) so an annotation command
 * (`add_annotation`/`set_annotation`/`remove_annotation`) sub-partitions to its own
 * per-author {@link annotationScope} — UNLESS the command's issuing actor resolves to a
 * GM via the optional `roleOf`, in which case it collapses to the base's `""` (gameplay)
 * scope instead: a GM's own doodle joins the ONE shared stack the GM's Ctrl+Z walks,
 * exactly like every other GM command, rather than landing on a `(gm, "annotation:gm")`
 * partition nothing ever reads. A **player's** annotation is unaffected either way — it
 * always sub-partitions per-author, so a player's map doodles still undo on their own,
 * independent of whatever the GM is rewinding. Every non-annotation encounter command
 * stays at the base's default scope (`""`); the ROOM (`backend/src/encounterRoom.ts`) is
 * what actually collapses gameplay's `""`-scope stack onto ONE constant key across every
 * actor — this class only supplies the scope classification both the room's push
 * (`historyActorId`) and its undo/redo (`undoPartitionFor`) read, so the two can never
 * diverge (a push filed under one partition that a later undo reads from another would
 * silently no-op — the reject-stale-as-no-op guard would just swallow it). Both of those
 * derive their `(key, scope)` from this one method (directly or via its `""` check), so
 * a change here keeps both sides agreeing by construction rather than by two edits kept
 * in sync.
 *
 * **`roleOf` is optional and absent-safe** — every non-`backend` construction site
 * (the browser client, `packages/ai`'s referee handle, `packages/rules`' scratch
 * executor, and every direct-`History` test fixture) has no roles to resolve and
 * passes none, so `scopeOf` falls back to the per-actor annotation scope for EVERY
 * actor there. An actor `roleOf` cannot resolve (returns `undefined` — an
 * unrecognized/stale slot) is treated as NOT a GM, never as GM — see {@link scopeOf}.
 *
 * `onApplied` also reads the command's OWN stamped `actorId` (via {@link commandActorId})
 * rather than trusting the `actorId` parameter the generic `History.applyCommand`/
 * `applyDetached` was called with — the shared stack passes a CONSTANT partition key as
 * that parameter for gameplay commands, which is correct for undo-stack bookkeeping but is
 * NOT who actually issued the command; the retained forward log (an audit trail) must still
 * byline the real actor. Every other domain's `onApplied` stays the base no-op, so this
 * divergence is invisible outside the encounter room.
 *
 * See `../commandHistory.ts` for the per-actor / reject-stale-as-no-op semantics.
 */
import { applyEncounter, type EncounterEvent } from "./reducer";
import type { EncounterCommand } from "./commands";
import type { CombatantBase, CombatantId, EncounterState } from "./types";
import type { ResolvedStep } from "./steps";
import { applyResolved } from "./applyResolved";
import type { Compendium } from "@lancer-vtt/compendium";
import { History, type ActorStacks, type UndoState } from "../commandHistory";
import { isInverseOnlyEncounterCommand } from "../inverseOnly";
import type { ActorRole } from "../permissions";

/**
 * The GM-role resolver `EncounterHistory` optionally accepts — the same shape as
 * `backend/src/commandRoom.ts`'s `RoleOf`, restated here rather than imported so
 * this package never depends on `backend`. Maps an
 * `actorId` to its slot role, or `undefined` when unresolvable (treated as "not a
 * GM" — see {@link EncounterHistory.scopeOf}).
 */
export type EncounterRoleOf = (actorId: string) => ActorRole;

/**
 * The per-author annotation undo scope: a
 * player's map doodles undo on their own, keyed to the authoring slot, never the
 * shared gameplay stack. Mirrors `../history.ts`'s `mechScope`/`pilotScope` shape —
 * the room's undo/redo entry point (`EncounterRoom`, `backend/src/encounterRoom.ts`)
 * derives the SAME scope string off the requesting slot so a push and its later undo
 * can never diverge onto different partitions.
 */
export function annotationScope(slotId: string): string {
  return `annotation:${slotId}`;
}

/**
 * The encounter undo-scope rule: which undo sub-partition a command lives in, derived
 * purely from the command. Two scopes:
 *
 *  - **`annotation:<slot>`** — a player-writable map annotation
 *    (`add_annotation`/`set_annotation`/`remove_annotation`), scoped to its OWN `actorId`
 *    (the author) so a player's doodles never pollute the GM's shared gameplay rewind.
 *  - **`""`** (the base default) — every other encounter command, i.e. gameplay. The
 *    room collapses this scope's stack onto ONE constant partition key shared by every
 *    actor — that collapse is the room's job (`historyActorId`/`SHARED_GAMEPLAY_KEY`
 *    in `backend/src/encounterRoom.ts`), not this function's; this function only tells
 *    the room WHICH commands are gameplay vs. annotation.
 */
export function scopeOfEncounterCommand(command: EncounterCommand): string {
  switch (command.type) {
    case "add_annotation":
    case "remove_annotation":
    case "set_annotation":
      return annotationScope(command.actorId);
    case "batch":
      // A batch's own scope is its first leaf's (mirrors `scopeOfRosterCommand` — encounter
      // batches are overwhelmingly internal composite inverses, never a mixed gameplay+
      // annotation submission in practice, so this is a totality fallback, not a load-bearing
      // rule.) An empty batch (never produced today) scopes to the gameplay default.
      return command.commands.length > 0 ? scopeOfEncounterCommand(command.commands[0]!) : "";
    default:
      return "";
  }
}

/**
 * A command's stamped `actorId`, read generically off the command object itself rather
 * than trusted from a caller-supplied parameter. Every
 * `EncounterCommand` leaf types `actorId` as a required field; the one exception is the
 * `batch` container (`Batch<Cmd>`, `shared/src/index.ts`) — it carries no `actorId` in
 * its OWN type, but `stampActor` (`backend/src/commandRoom.ts`) always stamps the SAME
 * real actor recursively across a whole command tree at the wire boundary, so a batch
 * object has one in practice even though the type doesn't declare it. `fallback` covers
 * the rare direct-`History` test fixture that builds a bare `{ type: "batch", commands }`
 * without ever routing through `stampActor`.
 */
function commandActorId(command: EncounterCommand, fallback: string): string {
  return (command as { actorId?: string }).actorId ?? fallback;
}

/**
 * The real issuing actor of a (possibly composite) command, read directly off the
 * command tree — the "is the shared stack's top entry MINE" check
 * (`EncounterRoom.topEntryIssuer`, `backend/src/encounterRoom.ts`) needs to
 * ask this of a raw stack entry with no caller-supplied fallback actor to hand it
 * (unlike {@link commandActorId}, which backs the post-apply forward-log hook and
 * always has the applying call's own `actorId` to fall back to). A `batch` recurses
 * into its FIRST child — mirroring `scopeOfEncounterCommand`'s own batch handling —
 * because every batch that ever lands on the shared gameplay stack (a single
 * `commitCompositeUndo` composite, or the reducer's own resolution batches) is
 * produced by exactly one real actor throughout the whole walk, so the first leaf's
 * stamped actor is the whole batch's. Returns `undefined` only for a pathological
 * empty batch (never produced today).
 */
export function issuerOfEncounterCommand(command: EncounterCommand): string | undefined {
  if (command.type === "batch") {
    const first = command.commands[0];
    return first === undefined ? undefined : issuerOfEncounterCommand(first);
  }
  return (command as { actorId?: string }).actorId;
}

/**
 * Walk a command tree (recursing into `batch`) and append a {@link ForwardLogEntry}
 * for every leaf command to the retained forward log, in application order, each
 * **tagged with its tier**:
 *
 *   - an `apply_resolved` → a `tier: "resolution"` entry carrying its recorded
 *     **step list** (the draw-and-record output — the dice said X);
 *   - any other (manual-tier) leaf → a `tier: "manual"` entry carrying **no steps**
 *     and the command `type` as its `kind` breadcrumb (a human set Y).
 *
 * Recording *both* makes the retained log a complete, honest audit trail that
 * distinguishes "the dice resolved this" from "a human fiat-set this" — the
 * resolution-vs-manual separation a replayable log depends on.
 * Inverse-only leaves (`isInverseOnlyEncounterCommand`) are skipped. Undo never reaches
 * this hook (`History.undo`/`redo` call the reducer directly, not `onApplied`), so the skip
 * does not guard against double-logging an undo: it governs a *forward* apply of a command
 * that exists only as an inverse, which no user issued and which would otherwise be bylined
 * as a human's `manual` edit.
 *
 * Replay (`replay()`) folds only `resolution` entries' steps; `manual` entries are
 * audit breadcrumbs (no resolved steps to fold) — see `replay()`.
 */
function appendLogEntries(
  log: ForwardLogEntry[],
  command: EncounterCommand,
  actorId: string,
): void {
  if (command.type === "batch") {
    for (const child of command.commands) appendLogEntries(log, child, actorId);
    return;
  }
  if (command.type === "apply_resolved") {
    log.push({
      tier: "resolution",
      actorId,
      kind: command.kind,
      steps: [...command.steps],
      ...(command.noBonus !== undefined ? { noBonus: command.noBonus } : {}),
      ...(command.forced === true ? { forced: true as const } : {}),
    });
    return;
  }
  // A forward apply of an inverse-only command is undo plumbing, not a user edit — don't log.
  if (isInverseOnlyEncounterCommand(command.type)) return;
  // A mutable-base edit is manual-tier
  // BUT must be *reproduced* by replay (the base is "frozen except via logged
  // edits"). Log it flagged manual carrying the edited base, so `replay()` re-applies
  // it in order — unlike an ordinary manual edit, which replay only audits.
  if (command.type === "edit_combatant_base") {
    log.push({
      tier: "manual",
      actorId,
      kind: command.type,
      steps: [],
      baseEdit: { combatantId: command.combatantId, base: command.base },
    });
    return;
  }
  // Every other leaf is a manual-tier edit (loose validation, GM/player fiat). Log
  // it flagged manual with no resolved steps — the audit trail records it happened,
  // replay ignores it (nothing to fold).
  log.push({ tier: "manual", actorId, kind: command.type, steps: [] });
}

/** Deep-ish copy of a log entry (steps + the optional baseEdit) so callers can't alias the internal log. */
function copyLogEntry(e: ForwardLogEntry): ForwardLogEntry {
  return {
    ...e,
    steps: [...e.steps],
    ...(e.baseEdit ? { baseEdit: { ...e.baseEdit } } : {}),
  };
}

/** Undo/redo availability for one actor (the generic {@link UndoState}). */
export type EncounterUndoState = UndoState;

/** One actor's undo + redo stacks of encounter commands. */
export type EncounterActorStacks = ActorStacks<EncounterCommand>;

/**
 * One entry in the retained forward log: the resolved step list of a single
 * resolution, plus a breadcrumb to what produced it and who issued it. This is
 * the **append-only replay/save/audit record** — distinct from the bounded undo stacks.
 */
export type ForwardLogEntry = {
  /**
   * Which command **tier** produced this entry:
   *   - `"resolution"` — an `apply_resolved` (the dice/rules resolved it); carries
   *     a non-empty `steps`. Folded by `replay()`.
   *   - `"manual"` — a manual-tier fiat edit (`set_hp`, `apply_status`, …) applied
   *     with loose validation; carries **no** `steps` (it's an audit breadcrumb,
   *     not a recorded resolution) and is **not** folded by `replay()`.
   * The marking is what lets a reader of the log tell "the dice said X" from "a
   * human set Y".
   */
  tier: "resolution" | "manual";
  /** The actor who issued the command. */
  actorId: string;
  /** Breadcrumb: the resolution kind (`"attack"`) or the manual command `type`. */
  kind?: string;
  /** The recorded resolved step list — the draw-and-record output of `resolve`. Empty for manual entries. */
  steps: ResolvedStep[];
  /**
   * Inert "no bonus damage" breadcrumb (Core p.71/p.70), carried through from
   * {@link "./commands".ApplyResolvedCommand.noBonus} unchanged — present (`true`) only on a
   * Skirmish/Barrage Aux rider's resolution entry, `undefined` on every primary/non-rider one. No
   * rule reads it yet — no bonus-damage suppression engine exists; it rides the log so a future
   * bonus-damage class has it to consume at replay time too, not just at first-resolve time.
   */
  noBonus?: boolean;
  /** A GM override resolution (`decisions/0209`), carried through from {@link "./commands".ApplyResolvedCommand.forced}. */
  forced?: true;
  /**
   * Present only on an `edit_combatant_base` entry: the combatant and the **new base**
   * the edit set. This is the
   * one manual-tier edit that `replay()` *reproduces* (not merely audits) — the base
   * is "frozen except via logged edits," so a faithful replay must re-apply each base
   * edit in log order. Absent on every other entry (resolution or ordinary manual).
   */
  baseEdit?: { combatantId: CombatantId; base: CombatantBase };
}

/** Named arguments for the {@link EncounterHistory} constructor. */
export type EncounterHistoryArgs = {
  state: EncounterState;
  compendium?: Compendium;
  history?: Record<string, EncounterActorStacks>;
  forwardLog?: ForwardLogEntry[];
  /**
   * Optional GM-role resolver — see {@link EncounterRoleOf} and the class doc's "Shared
   * encounter undo" note. Absent (every non-`backend` construction site), every actor
   * keeps the per-actor annotation scope.
   */
  roleOf?: EncounterRoleOf;
}

/**
 * In-memory encounter authority: the generic {@link History} bound to
 * `applyEncounter`, **plus** the retained forward log / replay (its real
 * encounter behavior, kept as an override — never flattened into the base).
 */
export class EncounterHistory extends History<EncounterState, EncounterCommand, EncounterEvent> {
  /**
   * The **retained forward log** — an **append-only** stream of
   * every *resolution*'s resolved step list, kept **alongside** the bounded
   * per-actor undo stacks and **never truncated to the undo horizon**. The undo
   * stacks are for editor-style undo over the live snapshot; *this* is the
   * complete, version-stable recording of the fight — simultaneously the replay
   * engine (`applyResolved` over the log), the encounter save format, and the
   * audit trail. Only `apply_resolved` commands append here (manual-tier edits do
   * not); a `batch` of resolutions appends one entry per `apply_resolved` child.
   */
  private readonly forwardLog: ForwardLogEntry[] = [];
  /** The read-only compendium threaded into every reducer call (optional — no current rule reads it). */
  readonly compendium?: Compendium;
  /** The optional GM-role resolver — see {@link EncounterHistoryArgs.roleOf}. */
  private readonly roleOf?: EncounterRoleOf;

  constructor(args: EncounterHistoryArgs) {
    const { state, compendium, history = {}, forwardLog = [], roleOf } = args;
    super((s, c) => applyEncounter(s, c, { compendium }), state, history);
    this.compendium = compendium;
    this.roleOf = roleOf;
    // Rehydrate the retained forward log. The undo stacks are memory-only (reset on
    // reboot), but the append-only forward log IS durable: a loaded encounter restores
    // its full replay/audit record so a reboot reproduces the fight exactly. Copy-in so
    // the caller's array can't alias the internal one.
    for (const entry of forwardLog) this.forwardLog.push(copyLogEntry(entry));
  }

  /**
   * The encounter-only post-apply behavior: append a tier-tagged entry per leaf to
   * the retained log. Runs only after a clean apply (the base calls it nowhere else)
   * — `resolution` entries carry the recorded step list, `manual` entries are audit
   * breadcrumbs. A batch contributes one entry per logged child, in order.
   *
   * **Reads the command's own stamped `actorId`, not the `actorId` parameter**
   * (see the class doc's "Shared encounter undo"
   * note): the room passes a CONSTANT partition key as that parameter for gameplay
   * commands so their undo bookkeeping shares one stack, but the forward log must
   * still byline the REAL issuing actor. `commandActorId` falls back to the
   * parameter when the command carries none (untyped on a bare `batch` fixture).
   */
  protected override onApplied(command: EncounterCommand, actorId: string): void {
    appendLogEntries(this.forwardLog, command, commandActorId(command, actorId));
  }

  /**
   * Which undo sub-partition `command` lives in — see {@link scopeOfEncounterCommand}
   * for the pure command-only classification. The base's default (`""`) is what every
   * gameplay command keeps; `EncounterRoom` is what actually collapses that scope's
   * per-actor stacks onto one shared constant key across every actor. A **player's**
   * annotation command sub-partitions to its own `annotation:<slot>`, unchanged.
   *
   * **A GM's own annotation collapses to `""` (gameplay) instead** — the one
   * behavioral addition this override makes over the pure function. `roleOf` is
   * consulted only when the pure scope is non-empty (i.e. only for an annotation
   * command; every other command is already `""` and returns early, untouched) and
   * only against the command's real ISSUING actor ({@link issuerOfEncounterCommand} —
   * recurses into a `batch`'s first leaf, mirroring `scopeOfEncounterCommand`'s own
   * batch handling, so a GM's multi-stroke erase batch resolves the SAME way its
   * leaves would individually). `roleOf` absent, or returning `undefined` for an
   * unresolvable actor, is treated as **not** a GM — never assume GM — so every
   * `roleOf`-less construction site (the browser client, `packages/ai`, `packages/rules`,
   * every direct-`History` test fixture) and every un-gated/test room keeps the plain
   * per-actor annotation scope.
   */
  override scopeOf(command: EncounterCommand): string {
    const scope = scopeOfEncounterCommand(command);
    if (scope === "") return scope;
    const issuer = issuerOfEncounterCommand(command);
    if (issuer !== undefined && this.roleOf?.(issuer) === "gm") return "";
    return scope;
  }

  /**
   * The retained forward log: a copy of every resolution's recorded
   * step list, in application order, never truncated. This is the replay/save/audit
   * stream — `replay()` folds it; a future durable store persists it. Returns a
   * shallow copy so a caller can't mutate the internal log.
   */
  resolvedLog(): ForwardLogEntry[] {
    return this.forwardLog.map(copyLogEntry);
  }

  /**
   * Replay the whole retained log onto a starting state, **rules-free**
   * (`applyResolved` over every logged step list, in order). This is the
   * version-stable replay engine: it reproduces the fight's resolved outcomes from
   * the record, never re-running the rules. Starts from `from` (a caller-supplied
   * empty/initial encounter) — note the log records *resolutions* only, so a faithful
   * full replay also needs the manual-tier command stream; today's proofs validate
   * resolution replay against a state with the combatants already present.
   */
  replay(from: EncounterState): EncounterState {
    let cur = from;
    // Two kinds of entry replay:
    //   - `resolution` entries fold their recorded step list rules-free (the dice
    //     were already drawn).
    //   - an `edit_combatant_base` entry (a manual entry carrying `baseEdit`) is
    //     re-applied as the base edit it was, so the replayed combatant ends up with
    //     the SAME edited base — the base is "frozen *except* via logged edits," and
    //     this is what makes those logged edits reproduce. The reducer re-clamps
    //     `current` into the new bounds exactly as the live apply did → deterministic.
    // Every other manual entry is an audit breadcrumb (empty steps, no baseEdit) and
    // is skipped; a faithful full replay of *other* manual fiat still needs the
    // command stream.
    for (const entry of this.forwardLog) {
      if (entry.tier === "resolution") {
        cur = applyResolved(cur, entry.steps);
      } else if (entry.baseEdit) {
        // A logged base edit always re-applies cleanly under replay (the
        // combatant existed when it was first logged); a rejection here would be
        // a genuine bug, so unwrap and let an unexpected `err` surface as a throw.
        const r = applyEncounter(cur, {
          type: "edit_combatant_base",
          actorId: entry.actorId,
          combatantId: entry.baseEdit.combatantId,
          base: entry.baseEdit.base,
        });
        if (!r.ok) throw r.error;
        cur = r.value.state;
      }
    }
    return cur;
  }
}
